import MapboxDraw from "@mapbox/mapbox-gl-draw";
import isEmpty from "lodash/isEmpty";
import type { GeoJSONSource } from "mapbox-gl";
import { useEffect } from "react";
import {
    getAreaOutsideBoundaries,
    getMapDrawnFeatures,
    snapToView,
} from "../../utils/geometry-utils";
import { DEFAULT_SHAPE_COLOR, DEFAULT_SHAPE_OPACITY } from "./style-hooks";
import { useMap } from "../context";
import { MapContextPolygon, MapContextPolygonSource } from "../context/types";
import {
    BOUNDARIES_ADDITIONAL_OUTLINE_LAYER_ID,
    BOUNDARIES_FILL_LAYER_ID,
    BOUNDARIES_OUTLINE_LAYER_ID,
    BOUNDARIES_OUTSIDE_AREA_SOURCE_ID,
    BOUNDARIES_SOURCE_ID,
    OUTSIDE_AREA_LAYER_ID,
} from "../utils/boundaries-layers-utils";

export type SavedPolygonsHookProps = {
    style?: mapboxgl.Style;
    isSavedViewPage: boolean;
};

/**
 *
 * @description This hook is only to add the Boundaries layers to the Map. It doesn't set any states.
 */
export const useSavedPolygon = ({
    style,
    isSavedViewPage,
}: SavedPolygonsHookProps) => {
    const { map, polygons, draw, isMapLoaded } = useMap();

    const savedPolygons = polygons.filter(
        (polygon) => polygon.source === MapContextPolygonSource.boundary
    );

    const drawnPolygons = getMapDrawnFeatures(draw, isMapLoaded);

    const onSavedAreasChange = (savedAreaPolygons: MapContextPolygon[]) => {
        const current = map.current;

        if (current && isMapLoaded) {
            const source = map.current?.getSource(
                BOUNDARIES_SOURCE_ID
            ) as GeoJSONSource;

            const maskSource = map.current?.getSource(
                BOUNDARIES_OUTSIDE_AREA_SOURCE_ID
            ) as GeoJSONSource;

            const features = savedAreaPolygons.flatMap((s) => s.feature);

            if (!isEmpty(savedAreaPolygons)) {
                const outsideArea = getAreaOutsideBoundaries(features);

                const savedPolygonsCollection = {
                    type: MapboxDraw.constants.geojsonTypes.FEATURE_COLLECTION,
                    features,
                };

                if (source) {
                    source.setData(savedPolygonsCollection);
                }

                if (outsideArea && maskSource) {
                    maskSource.setData(outsideArea);
                }

                const drawnFeatures = drawnPolygons.map((d) => d.feature);

                // When it's a SV, we don't want it to recenter on the Saved Areas. We want it to take the viewport saved in the SV.
                if (!isSavedViewPage) {
                    snapToView({
                        features: [...features, ...drawnFeatures],
                        map,
                    });
                }
            } else {
                const defaultData: GeoJSON.FeatureCollection<
                    GeoJSON.Geometry,
                    GeoJSON.GeoJsonProperties
                > = {
                    type: "FeatureCollection",
                    features: [],
                };

                if (source) {
                    source.setData(defaultData);
                }

                if (maskSource) {
                    maskSource.setData(defaultData);
                }
            }
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        onSavedAreasChange(savedPolygons);
    }, [savedPolygons.length, isMapLoaded, style?.sprite]);

    const onStyleLoad = (map: mapboxgl.Map) => {
        map.addSource(BOUNDARIES_SOURCE_ID, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: [],
            },
        });

        map.addSource(BOUNDARIES_OUTSIDE_AREA_SOURCE_ID, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: [],
            },
        });

        map.addLayer({
            id: OUTSIDE_AREA_LAYER_ID,
            type: "fill",
            source: BOUNDARIES_OUTSIDE_AREA_SOURCE_ID,
            interactive: false,
            paint: {
                "fill-color": "#D0D5DD",
                "fill-opacity": 0.5,
            },
        });

        map.addLayer({
            id: BOUNDARIES_OUTLINE_LAYER_ID,
            type: "line",
            source: BOUNDARIES_SOURCE_ID,
            interactive: false,
            paint: {
                "line-color": [
                    "coalesce",
                    ["get", "stroke-color"],
                    DEFAULT_SHAPE_COLOR,
                ],
                "line-width": ["coalesce", ["get", "stroke-width"], 2],
                "line-opacity": ["coalesce", ["get", "stroke-opacity"], 1],
            },
        });

        // An additional invisible outline to stop the `not-allowed`
        // cursor from showing once it gets close to a saved area.
        map.addLayer({
            id: BOUNDARIES_ADDITIONAL_OUTLINE_LAYER_ID,
            type: "line",
            source: BOUNDARIES_SOURCE_ID,
            interactive: false,
            paint: {
                "line-color": DEFAULT_SHAPE_COLOR,
                "line-width": 10,
                "line-opacity": 0,
            },
        });

        map.addLayer({
            id: BOUNDARIES_FILL_LAYER_ID,
            type: "fill",
            source: BOUNDARIES_SOURCE_ID,
            interactive: false,
            paint: {
                "fill-color": [
                    "coalesce",
                    ["get", "fill-color"],
                    DEFAULT_SHAPE_COLOR,
                ],
                "fill-opacity": [
                    "coalesce",
                    ["get", "fill-opacity"],
                    DEFAULT_SHAPE_OPACITY,
                ],
            },
        });
    };

    return { onStyleLoad };
};
